import { EmployeeRegistryClient } from "@/Common/Api/Employee/EmployeeRegistryClient";
import { realTimeNotificationClient } from "@/Common/Api/RealTimeNotification/SignalRClient";
import { EmployeeType } from "@/Common/Models/Employee/Enums/EmployeeType";
import { AuthenticatorCodeDeliveryMethod } from "@/Common/Models/MultifactorAuthentication/AuthenticatorCodeDeliveryMethod";
import { ILoginModel } from "@/Common/Models/UI/Login/ILoginModel";
import * as ServerOperations from "@/Common/Operations/operations.server";
import * as SubServerOperations from "@/Common/Operations/operations.server.sub";
import { BaseAction } from "@/Common/Stores/Actions/BaseAction";
import { ContactDirectoryClient } from "@/ContactDirectory/Api/ContactDirectoryClient";

class LoginAction extends BaseAction {
    constructor() {
        super();
    }

    public async login(model: ILoginModel): Promise<void> {
        const username = model.get("UserName");
        var requestData = {
            UserName: username,
            Password: model.get("Password"),
            AuthenticatorCode: model.get("AuthenticatorCode"),
            SkipAuthenticator: model.get("AuthenticatorCodeSkipChecked"),
            DeviceFingerprint: await sessionStore.getDeviceFingerprint()
        };
        this.Execute(ServerOperations.Server.Myle_Login, requestData);
    }

    public logout(withSessionCheck: boolean): void {
        this.clearCachedTokens();
        if (withSessionCheck)
            this.Execute(ServerOperations.Server.Myle_Logoff, null, SubServerOperations.Login.LogoutWithSessionCheck);
        else
            this.Execute(ServerOperations.Server.Myle_Logoff, null, SubServerOperations.Login.LogoutToHomeScreen);

        realTimeNotificationClient.disconnect();
    }

    public async resume(model: ILoginModel): Promise<void> {
        const username = model.get("UserName");
        var requestData = {
            UserName: username,
            Password: model.get("Password"),
            AuthenticatorCode: model.get("AuthenticatorCode"),
            SkipAuthenticator: model.get("AuthenticatorCodeSkipChecked"),
            DeviceFingerprint: await sessionStore.getDeviceFingerprint()
        };
        this.Execute(ServerOperations.Server.ResumeSession, requestData);
    }

    public loadCache(includeMedfarDictionaryDoctorStuff: boolean): void {
        var requestData = {
            IncludeSites: true,
            IncludeEmployees: true,
            IncludeLookups: true,
            IncludeUserSettings: true,
            IncludeDocumentSources: true,
            IncludeEmployeeGroups: true,
            IncludeServices: true,
            UserID: sessionStore.userId,
            IncludeMedfarDictionaryDoctorStuff: includeMedfarDictionaryDoctorStuff,
            IncludeScreeningPlanResources: true,
            IncludeCanadianTriageAndAcuityScore: cacheStore.ClinicSettings?.EnableCanadianTriageAndAcuityScore ?? false,
            IncludeQuestionnaireResources: cacheStore.ClinicSettings?.EnableCovidQuestionnaire ?? false,
            IncludeGrowthChartsCachedDate: sessionStore.hasRightsForAction([EmployeeType.Practician]),
            IncludePreventiveCareCriteriaList: cacheStore.ClinicSettings?.EnableRecallList
        };

        this.Execute(ServerOperations.Server.Myle_LoadCache, requestData);
    }

    public loadTitlesAndSpecialtiesCache(): void {
        if (cacheStore.TitlesAndSpecialtiesCache == null)
            this.Execute(ServerOperations.Server.MedfarDictionnary_SearchSpecialisation, { Country: sessionStore.getCurrentEmployee().getActiveSiteCountryLookupCode() });
    }

    public loadUserSettings(loginId: string): void {
        this.Execute(ServerOperations.Server.Myle_LoadUserSettings, { LoginId: loginId });
    }

    public loadClinicSettings(): void {
        this.Execute(ServerOperations.Server.MedfarDictionnary_LoadClinicSetting, null);
    }

    public checkSession(): void {
        var requestData = { CheckSession: true };
        this.Execute(ServerOperations.Server.SessionCheck, requestData);
    }

    public acceptAgreement(): void {
        this.Execute(ServerOperations.Server.Myle_AcceptAgreement, null);
    }

    public updatePromptedForRecoveryEmailAttribute(): void {
        this.Execute(ServerOperations.Server.Myle_UpdatePromptedForRecoveryEmailAttribute, null);
    }

    public updatePromptedDateForLoginMultifactionAuthenticator(): void {
        this.Execute(ServerOperations.Server.Myle_MultifactorAuthenticationModule_Login_SkipSetup, null);
    }

    public activateMultifactorAuthentication(request: { setupToken: string, authenticationCode: string, codeDeliveryMethod: AuthenticatorCodeDeliveryMethod, password: string }): void {
        const requestData = {
            SetupToken: request.setupToken,
            AuthenticationCode: request.authenticationCode,
            CodeDeliveryMethod: request.codeDeliveryMethod,
            Password: request.password
        };
        this.Execute(ServerOperations.Server.Myle_MultifactorAuthenticationModule_CurrentUser_Activate, requestData);
    }

    public verifyEmailAddress(emailAddress: string, contactPointId: string, verificationCode: string): void {
        var requestData = {
            Address: emailAddress,
            ContactPointId: contactPointId,
            VerificationCode: verificationCode
        };

        this.Execute(ServerOperations.Server.Myle_Verify_Email, requestData);
    }

    public sendPasswordReset(emailAddress: string, language: string): void {
        var requestData = {
            Address: emailAddress,
            Language: language
        };

        this.Execute(ServerOperations.Server.Myle_PwdRecovery_Password_Reset_Email, requestData);
    }

    public saveUserType(id: string, contactId: string, licenseOrganizationRuleId: string, licenseOrganizationSpecializationId: string): void {
        this.Execute(
            ServerOperations.Server.UpdateEmployeeType,
            {
                EmployeeId: id,
                ContactId: contactId,
                LicenseOrganizationRuleId: licenseOrganizationRuleId,
                LicenseOrganizationSpecializationId: licenseOrganizationSpecializationId
            }
        );
    }

    public getAccessInfo(): void {
        this.Execute(ServerOperations.Server.Myle_AuthenticationModule_GetAccessInfo, null);
    }

    private clearCachedTokens(): void {
        ContactDirectoryClient.getInstance().clearCache();
        EmployeeRegistryClient.getInstanceV1().clearCache();
        EmployeeRegistryClient.getInstanceV2().clearCache();
    }
}

export { LoginAction };
